import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, UserCircle, LogOut, Shield, Star, Users } from "lucide-react";
import { useAuth } from "@/hooks/auth/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

interface AccountDropdownProps {
  handleLogout: (e: React.MouseEvent) => void;
}

function getInitials(profile: { display_name?: string | null } | null, email: string | undefined): string {
  const name = profile?.display_name || email || "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const VIEW_MODES = {
  admin: { label: 'Admin', icon: Shield, color: undefined },
  beta: { label: 'Beta Tester', icon: Star, color: '#622066' },
  regular: { label: 'Regular User', icon: Users, color: '#8B6B14' },
} as const;

const AccountDropdownComponent = ({ handleLogout }: AccountDropdownProps) => {
  const { user, profile, isAdmin, viewAsRole, setViewAsRole } = useAuth();
  const navigate = useNavigate();

  const initials = getInitials(profile, user?.email);
  const avatarUrl = (profile as { avatar_url?: string | null } | null)?.avatar_url;
  const activeMode = VIEW_MODES[viewAsRole];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Account menu"
          className="
            flex items-center justify-center
            w-9 h-9 rounded-full
            border-2 border-yarn-orchid/30
            bg-yarn-quartz hover:bg-white/80
            text-yarn-slate font-semibold text-sm
            overflow-hidden
            transition-colors duration-150
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1
          "
          style={viewAsRole !== 'admin' ? { borderColor: activeMode.color } : undefined}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={initials} className="w-full h-full object-cover" />
          ) : (
            <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{initials || <UserCircle className="h-5 w-5" />}</span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate("/account")}
        >
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <span>Account</span>
        </DropdownMenuItem>

        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              View as...
            </DropdownMenuLabel>
            {(Object.keys(VIEW_MODES) as Array<keyof typeof VIEW_MODES>).map((key) => {
              const mode = VIEW_MODES[key];
              const Icon = mode.icon;
              const isActive = viewAsRole === key;
              return (
                <DropdownMenuItem
                  key={key}
                  className="flex items-center gap-2 cursor-pointer"
                  style={isActive && mode.color ? { color: mode.color } : undefined}
                  onClick={() => setViewAsRole(key)}
                >
                  <Icon
                    className="h-4 w-4"
                    style={isActive && mode.color ? { color: mode.color } : { color: undefined }}
                  />
                  <span>{mode.label}</span>
                  {isActive && <span className="ml-auto text-xs">Active</span>}
                </DropdownMenuItem>
              );
            })}
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

AccountDropdownComponent.displayName = "AccountDropdown";
export const AccountDropdown = memo(AccountDropdownComponent);
